import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '@/services/api'
import type { GenerationJobStatus } from '@/services/types'

const STORAGE_KEY = 'generation_job_id'
const POLL_INTERVAL = 2000

interface UseGenerationJobReturn {
  jobId: number | null
  status: GenerationJobStatus | null
  message: string | null
  errorMessage: string | null
  isGenerating: boolean
  startJob: (jobId: number) => void
  clearJob: () => void
}

function loadPersistedJobId(): number | null {
  const raw = sessionStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

export function useGenerationJob(): UseGenerationJobReturn {
  const [jobId, setJobId] = useState<number | null>(loadPersistedJobId)
  const [status, setStatus] = useState<GenerationJobStatus | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  const clearJob = useCallback(() => {
    stopPolling()
    sessionStorage.removeItem(STORAGE_KEY)
    setJobId(null)
    setStatus(null)
    setMessage(null)
    setErrorMessage(null)
  }, [stopPolling])

  const pollStatus = useCallback(async (id: number) => {
    try {
      const response = await api.getGenerationJobStatus(id)
      setStatus(response.status)
      setMessage(response.message ?? null)
      setErrorMessage(response.error_message ?? null)

      if (response.status === 'completed' || response.status === 'failed') {
        stopPolling()
      }
    } catch {
      // Keep polling on transient errors
    }
  }, [stopPolling])

  const startPolling = useCallback((id: number) => {
    stopPolling()
    // Poll immediately, then on interval
    pollStatus(id)
    pollingRef.current = setInterval(() => pollStatus(id), POLL_INTERVAL)
  }, [stopPolling, pollStatus])

  const startJob = useCallback((id: number) => {
    sessionStorage.setItem(STORAGE_KEY, String(id))
    setJobId(id)
    setStatus('pending')
    setMessage(null)
    setErrorMessage(null)
    startPolling(id)
  }, [startPolling])

  // Resume polling on mount if there's a persisted job
  useEffect(() => {
    if (jobId && !pollingRef.current) {
      startPolling(jobId)
    }
    return stopPolling
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isGenerating = status === 'pending' || status === 'processing'

  return { jobId, status, message, errorMessage, isGenerating, startJob, clearJob }
}
